import { useEffect, useMemo, useState } from "react";
import { Search, Plus, Lock, Filter, ShoppingBag, ArrowRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useApp } from "@/store/useApp";
import { ProductOfferModal } from "@/components/ProductOfferModal";
import type { Product } from "@/lib/types";
import { cn } from "@/lib/utils";
import { StatusBadge } from "@/components/StatusBadge";
import { cheapestOfferedCombo } from "@/lib/catalogPricing";

interface Props {
  processedOnly?: boolean;
}

export default function Catalog({ processedOnly = false }: Props) {
  const { account, cart, products, catalogCategories, catalogStatus, catalogUnits, hydrateCatalog, setCartOpen } = useApp();
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState<string>("ALL");
  const [selected, setSelected] = useState<Product | null>(null);

  const validated = account?.status === "VALIDADO";

  useEffect(() => {
    if (catalogStatus === "loading" || (catalogStatus === "ready" && products.length > 0)) return;
    void hydrateCatalog();
  }, [catalogStatus, hydrateCatalog, products.length]);

  const categories = useMemo(
    () => [
      { id: "ALL", label: "Todos", emoji: "🌱" },
      ...catalogCategories.map((c) => ({ id: c.id, label: c.label, emoji: c.emoji })),
    ],
    [catalogCategories],
  );

  const filtered = useMemo(() => {
    return products
      .filter((p) => {
        if (p.active === false) return false;
        if (processedOnly && !p.isProcessed) return false;
        if (category !== "ALL" && p.category !== category) return false;
        if (query && !p.name.toLowerCase().includes(query.toLowerCase())) return false;
        return true;
      })
      .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));
  }, [query, category, processedOnly, products]);

  const inCart = (productId: string) => cart.filter((c) => c.productId === productId).length;
  const cartCount = cart.reduce((acc, item) => acc + item.quantity, 0);
  const hasCartItems = cart.length > 0;

  return (
    <>
      {/* Hero band */}
      <section className="bg-gradient-hero text-primary-foreground">
        <div className="container py-8 lg:py-10">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6">
            <div className="max-w-2xl space-y-3">
              <div className="inline-flex items-center gap-2 rounded-full border border-primary-foreground/20 bg-primary-foreground/10 backdrop-blur-sm px-3 py-1 text-xs font-medium">
                {processedOnly ? "Linha processada" : "Catálogo geral"}
              </div>
              <h1 className="font-display text-3xl lg:text-4xl font-bold tracking-tight">
                {processedOnly ? "Produtos processados, prontos para a sua cozinha" : "Hortifrúti fresco para o seu negócio"}
              </h1>
              <p className="text-primary-foreground/80 text-sm lg:text-base">
                {processedOnly
                  ? "Cortes higienizados, embalagens prontas e mix prontos para acelerar a operação."
                  : "Selecione os melhores hortifrútis para restaurante, supermercado ou cozinha industrial."}
              </p>
            </div>
            {account && (
              <div className="shrink-0">
                <StatusBadge status={account.status} className="bg-primary-foreground text-primary border-transparent" />
              </div>
            )}
          </div>
        </div>
      </section>

      <div className="container py-6 lg:py-8 space-y-6 pb-28 sm:pb-8">
        {/* Search */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar produtos..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="pl-9 h-12 bg-surface text-base"
            />
          </div>
        </div>

        {/* Categories */}
        <div className="flex items-center gap-2 overflow-x-auto pb-1 -mx-1 px-1 scrollbar-hide">
          <Filter className="h-4 w-4 text-muted-foreground shrink-0" />
          {categories.map((c) => (
            <Button
              key={c.id}
              variant="chip"
              size="sm"
              data-active={category === c.id}
              onClick={() => setCategory(c.id)}
              className="shrink-0 h-9"
            >
              <span>{c.emoji}</span>
              {c.label}
            </Button>
          ))}
        </div>

        {/* Result count */}
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground tabular-nums">{filtered.length} produtos</span>
          {!validated && (
            <span className="hidden sm:inline-flex items-center gap-1.5 text-xs text-muted-foreground">
              <Lock className="h-3.5 w-3.5" /> Preços liberam após validação comercial
            </span>
          )}
        </div>

        {catalogStatus === "loading" && products.length === 0 && (
          <div className="surface-card p-8 text-center text-sm text-muted-foreground">
            Carregando catálogo...
          </div>
        )}

        {/* Product list */}
        <div className="space-y-2">
          {filtered.map((p) => {
            const count = inCart(p.id);
            // Resumo "a partir de": menor preço entre as combinações REAIS (produto+unidade+corte) do
            // produto — determinístico e nunca convertido/inventado. Sem combinação com preço, não exibe.
            const cheapest = validated ? cheapestOfferedCombo(p, catalogUnits) : undefined;
            const priceUnitName = cheapest?.unitId
              ? catalogUnits.find((unit) => unit.id === cheapest.unitId)?.name ?? p.defaultUnit
              : p.defaultUnit;
            return (
              <div
                key={p.id}
                className={cn(
                  "group surface-card p-3 sm:p-4 flex items-center gap-3 sm:gap-4 hover:border-primary/30 hover:shadow-md transition-all cursor-pointer",
                  count > 0 && "border-primary/40 bg-primary/[0.02]",
                )}
                onClick={() => setSelected(p)}
              >
                <div className="h-12 w-12 sm:h-14 sm:w-14 rounded-lg bg-secondary flex items-center justify-center text-2xl sm:text-3xl shrink-0 overflow-hidden">
                  {p.imageUrl ? (
                    <img src={p.imageUrl} alt={p.name} className="h-full w-full object-cover" />
                  ) : (
                    <span>{p.emoji ?? "🌱"}</span>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-display font-semibold uppercase text-sm sm:text-base tracking-wide">
                      {p.name}
                    </h3>
                    {p.isProcessed && (
                      <span className="text-[10px] font-bold tracking-wider uppercase text-primary bg-primary/10 px-1.5 py-0.5 rounded">
                        Processado
                      </span>
                    )}
                  </div>
                  <div className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
                    <span>{p.defaultUnit}</span>
                    {validated && cheapest != null && (
                      <>
                        <span className="text-border">•</span>
                        <span className="text-primary font-medium">
                          a partir de R$ {cheapest.price.toFixed(2)}/{priceUnitName}
                        </span>
                      </>
                    )}
                    {count > 0 && (
                      <>
                        <span className="text-border">•</span>
                        <span className="text-success font-medium">
                          {count} {count === 1 ? "config." : "configs."} no carrinho
                        </span>
                      </>
                    )}
                  </div>
                </div>
                <Button variant="hero" size="sm" className="shrink-0" onClick={(e) => { e.stopPropagation(); setSelected(p); }}>
                  <Plus className="h-4 w-4" />
                  <span className="hidden sm:inline">Adicionar</span>
                </Button>
              </div>
            );
          })}

          {filtered.length === 0 && (
            <div className="surface-card p-12 text-center">
              <Search className="mx-auto h-8 w-8 text-muted-foreground mb-3" />
              <h3 className="font-display font-semibold">Nenhum produto encontrado</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Tente outra busca ou remova o filtro de categoria.
              </p>
            </div>
          )}
        </div>
      </div>

      <ProductOfferModal
        product={selected}
        open={!!selected}
        onOpenChange={(o) => !o && setSelected(null)}
      />

      {/* Mobile sticky cart CTA */}
      <div className="fixed inset-x-0 bottom-0 z-30 px-4 pb-[max(env(safe-area-inset-bottom),1rem)] pt-3 sm:hidden pointer-events-none">
        <div className="pointer-events-auto mx-auto max-w-md">
          <Button
            variant="hero"
            onClick={() => setCartOpen(true)}
            className={cn(
              "w-full shadow-lg shadow-primary/30 transition-all",
              hasCartItems ? "h-14 text-base" : "h-12",
            )}
          >
            <ShoppingBag className="h-5 w-5" />
            {hasCartItems ? (
              <>
                <span className="font-semibold">
                  {cartCount} {cartCount === 1 ? "item" : "itens"} no carrinho
                </span>
                <span className="ml-auto inline-flex items-center gap-1 text-sm font-medium">
                  Finalizar <ArrowRight className="h-4 w-4" />
                </span>
              </>
            ) : (
              <span className="font-medium">Ver meu carrinho</span>
            )}
          </Button>
        </div>
      </div>
    </>
  );
}
